/**
 * Reports hub — links to the 5 admin reports.
 *
 * Lives at /app/reports. Each card is a thin wrapper that fetches the
 * corresponding /api/admin/reports/* endpoint and renders a focused view.
 */
import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { CurrencyInr, ReceiptX, Money, ChartLine, Scales } from "@phosphor-icons/react";

const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(n) || 0);

const ACCENT_CLASS = {
  danger: "text-[var(--vs-danger)]",
  success: "text-[var(--vs-success)]",
  default: "",
};

function Stat({ label, value, accent = "default", testId }) {
  const cls = ACCENT_CLASS[accent] || ACCENT_CLASS.default;
  return (
    <div className="vs-card p-4">
      <div className="eyebrow">{label}</div>
      <div className={`font-display text-2xl font-semibold mt-1 ${cls}`} data-testid={testId}>{typeof value === "number" ? fmt(value) : value}</div>
    </div>
  );
}

function FeesCollection() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/reports/fees-collection").then((r) => setData(r.data)).catch(() => setData({ total: 0, count: 0, by_school: [], by_hostel: [], by_period: [] })); }, []);
  if (!data) return <div className="text-sm">Loading…</div>;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Stat label="Total collected" value={data.total} accent="success" testId="fees-collection-total" />
        <Stat label="Count" value={data.count} />
        <Stat label="By period" value={`${data.by_period.length} period(s)`} />
      </div>
      <div className="vs-card p-4">
        <div className="eyebrow mb-2">By period</div>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]"><th className="py-2">Period</th><th className="py-2 text-right">Amount</th></tr></thead>
          <tbody>{data.by_period.map((p) => (<tr key={p.period} className="border-t border-[var(--vs-border)]"><td className="py-2">{p.period}</td><td className="py-2 text-right font-mono">{fmt(p.amount)}</td></tr>))}{data.by_period.length === 0 && <tr><td colSpan={2} className="py-6 text-center text-[var(--vs-text-secondary)]">No fees collected yet</td></tr>}</tbody>
        </table>
      </div>
    </div>
  );
}

function FeesPending() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/reports/fees-pending").then((r) => setData(r.data)).catch(() => setData({ total_due: 0, count: 0, items: [] })); }, []);
  if (!data) return <div className="text-sm">Loading…</div>;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Stat label="Total due" value={data.total_due} accent="danger" testId="fees-pending-total" />
        <Stat label="Pending invoices" value={data.count} />
      </div>
      <div className="vs-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--vs-bg-subtle)]"><tr className="text-left text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]"><th className="px-4 py-3">Period</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Amount</th><th className="px-4 py-3 text-right">Paid</th><th className="px-4 py-3 text-right">Due</th></tr></thead>
          <tbody>{data.items.slice(0, 100).map((it) => (<tr key={it.fee_id} className="border-t border-[var(--vs-border)]"><td className="px-4 py-3">{it.period}</td><td className="px-4 py-3"><Badge variant="outline">{it.status}</Badge></td><td className="px-4 py-3 text-right font-mono">{fmt(it.amount)}</td><td className="px-4 py-3 text-right font-mono">{fmt(it.paid)}</td><td className="px-4 py-3 text-right font-mono text-[var(--vs-danger)]">{fmt(it.due)}</td></tr>))}{data.items.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-[var(--vs-text-secondary)]">No pending fees 🎉</td></tr>}</tbody>
        </table>
      </div>
    </div>
  );
}

function ExpenditureSummary() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/reports/expenditure-summary").then((r) => setData(r.data)).catch(() => setData({ total: 0, count: 0, by_category: [], by_school: [], by_hostel: [] })); }, []);
  if (!data) return <div className="text-sm">Loading…</div>;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Stat label="Total expenditure" value={data.total} accent="danger" testId="exp-summary-total" />
        <Stat label="Voucher count" value={data.count} />
      </div>
      <div className="vs-card p-4">
        <div className="eyebrow mb-2">By category</div>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]"><th className="py-2">Category</th><th className="py-2 text-right">Amount</th></tr></thead>
          <tbody>{data.by_category.map((c) => (<tr key={c.category} className="border-t border-[var(--vs-border)]"><td className="py-2 capitalize">{c.category}</td><td className="py-2 text-right font-mono">{fmt(c.amount)}</td></tr>))}{data.by_category.length === 0 && <tr><td colSpan={2} className="py-6 text-center text-[var(--vs-text-secondary)]">No expenditure recorded yet</td></tr>}</tbody>
        </table>
      </div>
    </div>
  );
}

function PL() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/reports/pl").then((r) => setData(r.data)).catch(() => setData(null)); }, []);
  if (!data) return <div className="text-sm">Loading…</div>;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Stat label="Income" value={data.income.total} accent="success" testId="pl-income-total" />
        <Stat label="Expenditure" value={data.expenditure.total} accent="danger" />
        <Stat label="Net" value={data.net} accent={data.net >= 0 ? "success" : "danger"} testId="pl-net" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="vs-card p-4">
          <div className="eyebrow mb-2">Income breakdown</div>
          <table className="w-full text-sm">
            <tbody>
              <tr className="border-t border-[var(--vs-border)]"><td className="py-2">Donations</td><td className="py-2 text-right font-mono">{fmt(data.income.donations)}</td></tr>
              <tr className="border-t border-[var(--vs-border)]"><td className="py-2">Fees collected</td><td className="py-2 text-right font-mono">{fmt(data.income.fees_collected)}</td></tr>
              <tr className="border-t-2 border-[var(--vs-secondary)] font-semibold"><td className="py-2">Total</td><td className="py-2 text-right font-mono text-[var(--vs-success)]">{fmt(data.income.total)}</td></tr>
            </tbody>
          </table>
        </div>
        <div className="vs-card p-4">
          <div className="eyebrow mb-2">Expenditure by category</div>
          <table className="w-full text-sm">
            <tbody>
              {data.expenditure.by_category.map((c) => (
                <tr key={c.category} className="border-t border-[var(--vs-border)]"><td className="py-2 capitalize">{c.category}</td><td className="py-2 text-right font-mono">{fmt(c.amount)}</td></tr>
              ))}
              <tr className="border-t-2 border-[var(--vs-secondary)] font-semibold"><td className="py-2">Total</td><td className="py-2 text-right font-mono text-[var(--vs-danger)]">{fmt(data.expenditure.total)}</td></tr>
            </tbody>
          </table>
        </div>
      </div>
      <div className="text-sm text-[var(--vs-text-secondary)]">
        Verdict: <Badge variant="outline" className={data.verdict === "surplus" ? "border-[var(--vs-success)]/40 text-[var(--vs-success)]" : "border-[var(--vs-danger)]/40 text-[var(--vs-danger)]"}>{data.verdict.toUpperCase()}</Badge>
      </div>
    </div>
  );
}

function BalanceSheet() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/reports/balance-sheet").then((r) => setData(r.data)).catch(() => setData(null)); }, []);
  if (!data) return <div className="text-sm">Loading…</div>;
  return (
    <div className="space-y-4">
      <div className="text-xs text-[var(--vs-text-secondary)]">As of <span className="font-mono">{data.as_of}</span></div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="vs-card p-4">
          <div className="eyebrow mb-2">Assets</div>
          <table className="w-full text-sm">
            <tbody>
              <tr className="border-t border-[var(--vs-border)]"><td className="py-2">Cash on hand</td><td className="py-2 text-right font-mono">{fmt(data.assets.cash_on_hand)}</td></tr>
              <tr className="border-t border-[var(--vs-border)]"><td className="py-2">Receivables (fees)</td><td className="py-2 text-right font-mono">{fmt(data.assets.receivables_fees)}</td></tr>
              <tr className="border-t-2 border-[var(--vs-secondary)] font-semibold"><td className="py-2">Total</td><td className="py-2 text-right font-mono" data-testid="bs-assets-total">{fmt(data.assets.total)}</td></tr>
            </tbody>
          </table>
        </div>
        <div className="vs-card p-4">
          <div className="eyebrow mb-2">Liabilities</div>
          <div className="text-sm py-2">{data.liabilities.note}</div>
          <div className="font-display text-2xl mt-2">{fmt(data.liabilities.total)}</div>
        </div>
        <div className="vs-card p-4">
          <div className="eyebrow mb-2">Equity</div>
          <div className="font-display text-2xl mt-2" data-testid="bs-equity">{fmt(data.equity)}</div>
        </div>
      </div>
      <div className="vs-card p-4 text-xs text-[var(--vs-text-secondary)]">
        <div className="eyebrow mb-1">Components (lifetime)</div>
        Donations {fmt(data.components.donations_lifetime)} + Fees collected {fmt(data.components.fees_collected_lifetime)} − Expenditure {fmt(data.components.expenditure_lifetime)}
      </div>
    </div>
  );
}

function TrialBalance() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/reports/trial-balance").then((r) => setData(r.data)).catch(() => setData(null)); }, []);
  if (!data) return <div className="text-sm">Loading…</div>;
  return (
    <div className="space-y-4">
      <div className="text-xs text-[var(--vs-text-secondary)]">As of <span className="font-mono">{data.as_of}</span> · {data.balanced ? <Badge variant="outline" className="border-[var(--vs-success)]/40 text-[var(--vs-success)]">BALANCED</Badge> : <Badge variant="outline" className="border-[var(--vs-danger)]/40 text-[var(--vs-danger)]">UNBALANCED</Badge>}</div>
      <div className="vs-card overflow-x-auto" data-testid="trial-balance-table">
        <table className="w-full text-sm">
          <thead className="bg-[var(--vs-bg-subtle)]"><tr className="text-left text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]"><th className="px-4 py-3">Head</th><th className="px-4 py-3">Type</th><th className="px-4 py-3 text-right">Debit</th><th className="px-4 py-3 text-right">Credit</th></tr></thead>
          <tbody>
            {data.accounts.map((a) => (
              <tr key={`${a.head}-${a.type}`} className="border-t border-[var(--vs-border)]">
                <td className="px-4 py-2.5">{a.head}</td>
                <td className="px-4 py-2.5"><Badge variant="outline" className="text-[10px] capitalize">{a.type}</Badge></td>
                <td className="px-4 py-2.5 text-right font-mono">{a.debit ? fmt(a.debit) : "—"}</td>
                <td className="px-4 py-2.5 text-right font-mono">{a.credit ? fmt(a.credit) : "—"}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-[var(--vs-secondary)] font-semibold bg-[var(--vs-bg-subtle)]">
              <td className="px-4 py-3" colSpan={2}>Total</td>
              <td className="px-4 py-3 text-right font-mono">{fmt(data.total_debit)}</td>
              <td className="px-4 py-3 text-right font-mono">{fmt(data.total_credit)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Reports() {
  return (
    <DashboardLayout title="Reports" subtitle="Fees, expenditure, P&L, balance sheet, and trial balance — pragmatic v1 derived from existing collections.">
      <Tabs defaultValue="fees-collection">
        <TabsList>
          <TabsTrigger value="fees-collection" data-testid="rep-tab-fees-collection"><CurrencyInr size={12} className="mr-1.5" />Fees collected</TabsTrigger>
          <TabsTrigger value="fees-pending" data-testid="rep-tab-fees-pending"><ReceiptX size={12} className="mr-1.5" />Fees pending</TabsTrigger>
          <TabsTrigger value="expenditure" data-testid="rep-tab-expenditure"><Money size={12} className="mr-1.5" />Expenditure</TabsTrigger>
          <TabsTrigger value="pl" data-testid="rep-tab-pl"><ChartLine size={12} className="mr-1.5" />P&L</TabsTrigger>
          <TabsTrigger value="balance-sheet" data-testid="rep-tab-bs"><Scales size={12} className="mr-1.5" />Balance sheet</TabsTrigger>
          <TabsTrigger value="trial-balance" data-testid="rep-tab-tb">Trial balance</TabsTrigger>
        </TabsList>
        <TabsContent value="fees-collection"><FeesCollection /></TabsContent>
        <TabsContent value="fees-pending"><FeesPending /></TabsContent>
        <TabsContent value="expenditure"><ExpenditureSummary /></TabsContent>
        <TabsContent value="pl"><PL /></TabsContent>
        <TabsContent value="balance-sheet"><BalanceSheet /></TabsContent>
        <TabsContent value="trial-balance"><TrialBalance /></TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}
